import {ReactNode} from "react";
import {
    Box,
    BoxProps,
    HStack,
    Link,
    Text,
    Tooltip,
    VStack
} from "@chakra-ui/react";
import {InfoBoxProps, InfoBoxItem, ItemProps} from "../../models";

type Props = {
    icon?: string
    bg: string
    color: string
    content: string
} & BoxProps;
type InfoTooltipProps = {label: string,content: string,icon: string} & BoxProps;

export function TopicItem(p: ItemProps & BoxProps) {
    let node: ReactNode;
    if (p.node) {
        return <>{p.node}</>;
    }
    if (p.link) {
        node = <Link fontWeight="bold" color="primary.500" className="one-line" variant="underline" href={p.link} target="_blank" download="filename">
            {p.value}
        </Link>
    } else {
        node = <Text as={p.as ?? "strong"} {...p} className="one-line">
            {p.value}
        </Text>;
    }
    if (!p.icon) {
        return node;
    }
    return <HStack spacing=".25rem" {...p} as="div" color={p.color}>
        <svg width={p.s ?? "16"} height={p.s ?? "16"} aria-hidden="true" style={{flex: "0 0 auto"}}>
            <use xlinkHref={`#${p.icon}`} />
        </svg>
        <Text as={p.as ?? "strong"} textAlign={p.textAlign} className="one-line">{p.value}</Text>
    </HStack>
}

export function TopicIndicator(props: Props) {
    return <TopicItem
        height="42px"
        borderRadius={8}
        bg={props.bg}
        placeContent="center"
        alignItems="center"
        fontWeight={500}
        mx={props.mx}
        color={props.color}
        textTransform="capitalize"
        icon={props.icon}
        as="span"
        value={props.content}
        textAlign={props.textAlign}
    />;
}

export function Topic(props: InfoBoxItem & BoxProps) {
    const clone: any = Object.assign({}, props);
    delete clone.title;
    delete clone.items;
    return <Box className="topic-stack" {...clone}>
        <Text fontSize="14px" textTransform={props.noCase ? undefined : "capitalize"}>{props.title}</Text>
        {
            props.items.map(function (v, i) {
                if (v.value || v.node) {
                    return <TopicItem key={v.value ?? i} {...v}/>;
                }
                return <></>;
            })
        }
    </Box>;
}

export function InfoTooltip(p: InfoTooltipProps & {s?: string}) {
    return <HStack px={p.px} gap=".25rem">
        <Tooltip label={p.label} hasArrow placement="top" bg="primary.900">
            <svg width={p.s ?? "16"} height={p.s ?? "16"} aria-hidden="true" style={{flex: "0 0 auto"}}>
                <use xlinkHref={`#${p.icon}`} />
            </svg>
        </Tooltip>
        <Text className="one-line" fontSize="0.9em">{p.content}</Text>
    </HStack>;
}

export function InfoBox(p: InfoBoxProps) {
    return <VStack
        className="info-box"
        alignItems="stretch"
        flex={p.flex}
        overflowY={p.overflowY}
        maxHeight={p.maxHeight}
        sx={p.sx}
    >
        <TopicItem
            s="24"
            textTransform={p.noCase ? undefined : "capitalize"}
            value={p.title}
            icon={p.icon}
            color="primary.500"
            as="p"
        />
        <VStack
            alignItems="stretch"
            bgColor="#F5F7F9"
            borderRadius="8px"
            gap="1rem"
            p=".5rem .75rem"
            color="dark.500"
            w="100%"
        >
            {p.items.map((v, i) => <Topic key={i} {...v}/>)}
        </VStack>
    </VStack>
}
